import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ProgressSpinner } from 'primereact/progressspinner';
import { SinglePaymentsService } from '../../../../services/SinglePayments';
import type {
  SinglePaymentSupplier,
  StartSinglePaymentResponse
} from '../../../../services/SinglePayments';
import {
  getSinglePaymentId,
  getSinglePaymentToken
} from './singlePaymentSession';

type FormChoosePaymentSummaryProps = {
  data?: {
    currency?: {
      code?: string;
    };
  };
};

type SupplierVisualConfig = {
  label: string;
  logo?: string;
  logoClassName?: string;
  contentClassName?: string;
  helperText?: string;
};

const ebanxLogo = require('../../../../assets/images/ebanx-logo.svg').default;
const mercadoPagoLogo = require('../../../../assets/images/MP_RGB_HANDSHAKE_color_horizontal.svg').default;

const supplierVisualMap: Record<string, SupplierVisualConfig> = {
  ebanx: {
    label: 'Ebanx',
    logo: ebanxLogo,
    logoClassName: 'bg-contain',
    contentClassName: 'bg-white'
  },
  transbank: {
    label: 'Transbank',
    logo: require('../../../../assets/images/webpay.png'),
    logoClassName: 'bg-cover',
    contentClassName: 'bg-white'
  },
  mercadopago: {
    label: 'Mercado Pago',
    logo: mercadoPagoLogo,
    logoClassName: 'bg-contain',
    contentClassName: 'bg-white'
  },
  paypal: {
    label: 'PayPal',
    logo: require('../../../../assets/images/new-paypal-logo.jpg'),
    logoClassName: 'bg-contain',
    contentClassName: 'bg-white'
  },
  dlocal: {
    label: 'dLocal',
    logo: require('../../../../assets/images/dlocal-logo.jpg'),
    logoClassName: 'bg-contain',
    contentClassName: 'bg-white'
  }
};

function normalizeSupplierName(name: string): string {
  return String(name ?? '').trim().toLowerCase();
}

function getRedirectUrl(response: StartSinglePaymentResponse): string | undefined {
  return response?.redirect_url || response?.init_point || response?.sandbox_init_point;
}

function getVisibleErrorMessage(error: unknown, fallbackMessage: string): string {
  if (axios.isAxiosError(error)) {
    const responseMessage = error.response?.data?.message;
    if (typeof responseMessage === 'string' && responseMessage.trim() !== '') {
      return responseMessage;
    }
  }

  return fallbackMessage;
}

function capitalizeLabel(value: string): string {
  if (!value) {
    return 'Proveedor';
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
}

export const FormChoosePaymentSummary = ({ data }: FormChoosePaymentSummaryProps) => {
  const [isStartingPayment, setIsStartingPayment] = useState(false);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(true);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [suppliers, setSuppliers] = useState<SinglePaymentSupplier[]>([]);
  const [suppliersError, setSuppliersError] = useState('');
  const [startPaymentError, setStartPaymentError] = useState('');

  const currencyCode = data?.currency?.code ?? '';

  const loadSuppliers = async () => {
    const token = getSinglePaymentToken();
    const singlePaymentId = getSinglePaymentId();

    setIsLoadingSuppliers(true);
    setSuppliersError('');
    setStartPaymentError('');

    if (!token || !singlePaymentId) {
      setSuppliers([]);
      setSelectedSupplier('');
      setSuppliersError('No fue posible identificar el cobro. Recarga la página e inténtalo nuevamente.');
      setIsLoadingSuppliers(false);
      return;
    }

    try {
      const response = await SinglePaymentsService.GetPaymentSuppliers(token, singlePaymentId);
      const normalizedSuppliers = Array.isArray(response)
        ? response
            .map((supplier) => ({
              ...supplier,
              name: normalizeSupplierName(supplier.name)
            }))
            .filter((supplier) => supplier.name !== '')
            .filter(
              (supplier, index, currentSuppliers) =>
                currentSuppliers.findIndex(
                  (currentSupplier) => currentSupplier.name === supplier.name
                ) === index
            )
        : [];

      setSuppliers(normalizedSuppliers);
      setSelectedSupplier((currentSelection) => {
        if (
          currentSelection &&
          normalizedSuppliers.some((supplier) => supplier.name === currentSelection)
        ) {
          return currentSelection;
        }

        return normalizedSuppliers.length === 1 ? normalizedSuppliers[0].name : '';
      });

      if (normalizedSuppliers.length === 0) {
        setSuppliersError('No hay medios de pago disponibles para este cobro en este momento.');
      }
    } catch (error) {
      setSuppliers([]);
      setSelectedSupplier('');
      setSuppliersError(
        getVisibleErrorMessage(
          error,
          'No pudimos cargar las formas de pago disponibles. Inténtalo nuevamente en unos minutos.'
        )
      );
    } finally {
      setIsLoadingSuppliers(false);
    }
  }

  useEffect(() => {
    loadSuppliers();
  }, []);

  async function handleStartTransaction(): Promise<void> {
    const token = getSinglePaymentToken();
    const singlePaymentId = getSinglePaymentId();

    if (!selectedSupplier || !token || !singlePaymentId) {
      setStartPaymentError('Debes seleccionar una forma de pago válida para continuar.');
      return;
    }

    setIsStartingPayment(true);
    setStartPaymentError('');

    try {
      const response = await SinglePaymentsService.StartPaymentProcess(
        token,
        singlePaymentId,
        selectedSupplier
      );
      const redirectUrl = getRedirectUrl(response);

      if (!redirectUrl) {
        throw new Error('missing_redirect_url');
      }

      window.location.assign(redirectUrl);
    } catch (error) {
      setStartPaymentError(
        getVisibleErrorMessage(
          error,
          'No pudimos iniciar el pago. Intenta nuevamente o prueba con otra forma de pago.'
        )
      );
    } finally {
      setIsStartingPayment(false);
    }
  }

  const renderedSuppliers = useMemo(
    () =>
      suppliers.map((supplier, index) => {
        const supplierKey = supplier.id ?? `${supplier.name}-${index}`;
        const supplierName = normalizeSupplierName(supplier.name);
        const supplierVisual = supplierVisualMap[supplierName];
        const isSelected = selectedSupplier === supplierName;
        const label = supplierVisual?.label ?? capitalizeLabel(supplierName);

        return (
          <button
            key={supplierKey}
            className={`relative w-[200px] min-h-[112px] m-4 rounded-xl border-2 transition-all ${
              isSelected
                ? 'border-sky-500 shadow-xl ring-4 ring-sky-100'
                : 'border-transparent'
            }`}
            onClick={() => {
              setSelectedSupplier(supplierName);
              setStartPaymentError('');
            }}
            type='button'
          >
            <div
              className={`w-full h-full rounded-xl shadow-xl border border-gray-200 flex flex-col items-center justify-center text-center px-4 py-4 ${
                supplierVisual?.contentClassName ?? 'bg-white'
              }`}
            >
              {supplierVisual?.logo ? (
                <div
                  aria-label={label}
                  className={`w-full h-[52px] bg-center bg-no-repeat ${supplierVisual.logoClassName ?? 'bg-contain'}`}
                  style={{ backgroundImage: `url(${supplierVisual.logo})` }}
                ></div>
              ) : (
                <p className='font-bold text-lg text-slate-700'>{label}</p>
              )}
              {supplierVisual?.helperText && (
                <p className='text-sm text-gray-500 mt-2'>{supplierVisual.helperText}</p>
              )}
              {!supplierVisual?.logo && supplier.description && (
                <p className='text-sm text-gray-500 mt-2'>{supplier.description}</p>
              )}
            </div>
          </button>
        );
      }),
    [selectedSupplier, suppliers]
  );

  const actionLabel = selectedSupplier
    ? `Continuar con ${supplierVisualMap[selectedSupplier]?.label ?? capitalizeLabel(selectedSupplier)}`
    : 'Continuar al pago';

  return (
    <>
      <div>
        {isStartingPayment && (
          <div className='absolute top-0 left-0 w-full h-full bg-white z-10 grid place-items-center rounded-xl'>
            <div className='flex flex-col items-center'>
              <ProgressSpinner
                style={{ width: '60px', height: '60px' }}
                strokeWidth='7'
                fill='#ffffff'
                animationDuration='1.5s' />
              <h1 className='mt-5'>Estamos procesando tu pago...</h1>
            </div>
          </div>
        )}
        <div className=' flex items-center justify-center p-4'>
          <div className='w-full max-w-2xl'>
            <div className='shadow-sm bg-white border rounded-lg max-w-2xl w-full'>
              <div className='p-6 '>
                <div>
                  <h2 className='font-bold text-2xl'>
                    Formas de pago
                  </h2>
                  {currencyCode && (
                    <p className='text-gray-500 mt-2'>
                      Proveedores disponibles para esta cuota en <strong>{currencyCode}</strong>.
                    </p>
                  )}
                </div>
              </div>
              <div className='px-6'>
                <div data-orientation='horizontal' role='none' className='shrink-0 bg-gray-300 h-[1px] w-full'></div>
              </div>
              {suppliersError && (
                <div className='px-6 pt-6'>
                  <div className='rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700'>
                    {suppliersError}
                  </div>
                </div>
              )}
              {startPaymentError && (
                <div className='px-6 pt-6'>
                  <div className='rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800'>
                    {startPaymentError}
                  </div>
                </div>
              )}
              <div className={'p-5 flex justify-center flex-wrap'}>
                {isLoadingSuppliers ? (
                  <div className='py-8 flex flex-col items-center text-gray-500'>
                    <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth='6' />
                    <p className='mt-4'>Cargando medios de pago...</p>
                  </div>
                ) : (
                  renderedSuppliers
                )}
              </div>
              <div className='px-6 pb-6  flex flex-col'>
                <div className='flex flex-col items-end gap-3'>
                  {suppliersError && (
                    <button className='btn-prev' onClick={loadSuppliers} type='button'>
                      Reintentar carga
                    </button>
                  )}
                  <button
                    className='btn'
                    type='button'
                    onClick={handleStartTransaction}
                    disabled={selectedSupplier === '' || isLoadingSuppliers || isStartingPayment || suppliers.length === 0}
                  >
                    {actionLabel}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
